import React from 'react'
import {
  Button,
  Column,
  Columns,
  Container,
  TextArea,
} from '@resurs/midas-react'
import PropTypes from 'prop-types'
import TextInput from '../../../../midasComponents/components/TextInput'
import Page from '../../../../internal/Page'
import { getText } from '../../../../internal/TextLabels'
import styles from './styles.module.scss'
import { format } from '../../../../models/format'
import {
  ORDER_ALLOWED_QUANTITY,
  ORDER_COUNTRY_DISABLED,
  ORDER_IMAGE,
  ORDER_KEY,
  ORDER_TYPE,
} from '../constants'
import OrderLine from './OrderLine'
import { Countries } from '../../../../models/countries'

const filterShowOrderLine = (orderType, country) =>
  !ORDER_COUNTRY_DISABLED[orderType] ||
  !ORDER_COUNTRY_DISABLED[orderType].includes(country)

const renderMultipleOrderLines = (orderTypes, country, orderLines, onChange) =>
  orderTypes
    .filter(orderType => filterShowOrderLine(orderType, country))
    .map(orderType => (
      <OrderLine
        key={orderType}
        id={`orderLines.${ORDER_KEY[orderType]}`}
        label={getText(
          `support.pages.order.material.${ORDER_KEY[orderType]}.label`,
        )}
        value={orderLines[ORDER_KEY[orderType]]}
        values={ORDER_ALLOWED_QUANTITY[orderType]}
        image={ORDER_IMAGE[orderType] || null}
        onChange={onChange}
      >
        {getText(
          `support.pages.order.material.${ORDER_KEY[orderType]}.description`,
        )}
      </OrderLine>
    ))

const renderMultipleFormLinks = (orderTypes, country, onClick) =>
  orderTypes
    .filter(orderType => filterShowOrderLine(orderType, country))
    .map(orderType => (
      <OrderLine
        key={orderType}
        label={getText(
          `support.pages.order.material.${ORDER_KEY[orderType]}.label`,
        )}
      >
        {getText(
          `support.pages.order.material.${ORDER_KEY[orderType]}.description`,
        )}{' '}
        <a //eslint-disable-line
          title={getText('support.pages.order.material.forms.label')}
          onClick={onClick}
        >
          {getText('support.pages.order.material.forms.label')}
        </a>
      </OrderLine>
    ))

const OrderUI = ({
  userFullName,
  storeName,
  country,
  order,
  valid,
  onMoreFormsClick,
  onChange,
  onSubmit,
}) => (
  <Container grid960>
    <Columns>
      <Column col1 colMd0 />
      <Column col10 colMd12>
        <Container>
          <div style={{ paddingBottom: '1rem' }}>
            <h3>{getText('support.pages.order.title')}</h3>
            <div>{getText('support.pages.order.description')}</div>
          </div>
        </Container>
        <Page>
          <div
            style={{
              padding: '1rem',
            }}
          >
            <Container>
              <Columns>
                <Column col9>
                  <h5>{getText('support.pages.order.address.title')}</h5>
                  <p>
                    {userFullName}, {storeName}
                  </p>
                  <div className={styles.addressRow}>
                    <div className={styles.addressCol}>
                      <TextInput
                        id="address.street"
                        label={getText(
                          'support.pages.order.address.street.label',
                        )}
                        placeholder={getText(
                          'support.pages.order.address.street.placeholder',
                        )}
                        value={order.address.street}
                        required={getText(
                          'support.pages.order.address.street.required',
                        )}
                        format={format.string}
                        onChange={e => onChange(e.target.id, e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.addressRow}>
                    <div
                      className={styles.addressCol}
                      style={{ marginRight: '1rem' }}
                    >
                      <TextInput
                        id="address.zipCode"
                        label={getText(
                          'support.pages.order.address.zipCode.label',
                        )}
                        placeholder={getText(
                          'support.pages.order.address.zipCode.placeholder',
                        )}
                        value={order.address.zipCode}
                        required={getText(
                          'support.pages.order.address.zipCode.required',
                        )}
                        format={format.zipCode}
                        onChange={e => onChange(e.target.id, e.target.value)}
                      />
                    </div>
                    <div className={styles.addressColExpanded}>
                      <TextInput
                        id="address.city"
                        label={getText(
                          'support.pages.order.address.city.label',
                        )}
                        placeholder={getText(
                          'support.pages.order.address.city.placeholder',
                        )}
                        value={order.address.city}
                        required={getText(
                          'support.pages.order.address.city.required',
                        )}
                        format={format.string}
                        onChange={e => onChange(e.target.id, e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.addressRow}>
                    <div className={styles.addressCol}>
                      <TextInput
                        id="email"
                        label={getText(
                          'support.pages.order.address.email.label',
                        )}
                        placeholder={getText(
                          'support.pages.order.address.email.placeholder',
                        )}
                        value={order.email}
                        format={format.email}
                        onChange={e => onChange(e.target.id, e.target.value)}
                      />
                    </div>
                  </div>
                </Column>
                <Column col9>
                  <h5>{getText('support.pages.order.material.title')}</h5>
                  {renderMultipleOrderLines(
                    [ORDER_TYPE.REPLY_ENVELOPE, ORDER_TYPE.FOLDER],
                    country,
                    order.orderLines,
                    onChange,
                  )}
                  {renderMultipleFormLinks(
                    [ORDER_TYPE.CREDIT_APPLICATION, ORDER_TYPE.PURCHASE_NOTE],
                    country,
                    onMoreFormsClick,
                  )}
                </Column>
                <Column col9>
                  <h5>{getText('support.pages.order.material.titleResurs')}</h5>
                  {country === Countries.FI && (
                    <p>{getText('support.pages.order.material.description')}</p>
                  )}
                  {renderMultipleOrderLines(
                    [
                      ORDER_TYPE.SIGN_A5,
                      ORDER_TYPE.SIGN_A4,
                      ORDER_TYPE.STICKER_ENTRANCE,
                      ORDER_TYPE.STICKER,
                      ORDER_TYPE.SIGN_SHELF,
                      ORDER_TYPE.TAG,
                    ],
                    country,
                    order.orderLines,
                    onChange,
                  )}
                </Column>
                <Column col9>
                  <h5>{getText('support.pages.order.other.title')}</h5>
                  <TextArea
                    id="message"
                    label={getText('support.pages.order.other.message.label')}
                    placeholder={getText(
                      'support.pages.order.other.message.placeholder',
                    )}
                    value={order.message}
                    onChange={e => onChange(e.target.id, e.target.value)}
                    skipValidation
                  />
                </Column>
              </Columns>
            </Container>
          </div>
        </Page>
        <Page>
          <div
            style={{
              padding: '2rem',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              id="order-submit"
              type="primary"
              disabled={!valid}
              onClick={() => onSubmit()}
            >
              {getText('support.pages.order.send')}
            </Button>
          </div>
        </Page>
      </Column>
    </Columns>
  </Container>
)

OrderUI.propTypes = {
  userFullName: PropTypes.string.isRequired,
  storeName: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
  valid: PropTypes.bool.isRequired,
  onMoreFormsClick: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default OrderUI
